/**
 * Resolves @bee/pattern-library's real color tokens at runtime, rather than
 * hand-copying hex values into this file. In a real browser, with
 * eds-tokens.generated.css loaded (see eds.ts), `getComputedStyle` on
 * `document.documentElement` resolves the full custom-property chain
 * (including nested `var()` references) down to a literal color — so bumping
 * @bee/pattern-library and re-running the build picks up new colors
 * automatically, with nothing to manually re-sync here.
 *
 * The FALLBACKS below exist only for environments that can't resolve CSS
 * custom properties the way a real browser does (JSDOM/Jest does not resolve
 * nested var() chains) or that render before the stylesheet has loaded. They
 * are the verified real token values for @bee/pattern-library@0.8.0 at the
 * time this was written (see eds-tokens.generated.css) — a safety net, not
 * the source of truth.
 */

export type EdsColorTokens = {
  brandMain: string;
  brandDark: string;
  errorMain: string;
  errorDark: string;
  warningMain: string;
  successMain: string;
  secondaryMain: string;
  textPrimary: string;
  textSecondary: string;
  backgroundDefault: string;
  backgroundPaper: string;
  divider: string;
  /**
   * Text/link color for content rendered directly on top of a solid banner
   * background (banner.info/error/warning are all saturated brand colors,
   * not the page background — see the mode-specific override below).
   */
  bannerText: string;
};

type EdsMode = 'light' | 'dark';

const DEFAULT_VAR_NAMES: Record<keyof EdsColorTokens, string> = {
  brandMain: '--colors-foreground-fg-brand-600',
  brandDark: '--colors-foreground-fg-brand-700',
  errorMain: '--colors-foreground-fg-error-600',
  errorDark: '--colors-foreground-fg-error-700',
  warningMain: '--colors-foreground-fg-warning-600',
  successMain: '--colors-foreground-fg-success-600',
  secondaryMain: '--colors-foreground-fg-gray-700',
  textPrimary: '--colors-foreground-fg-gray-900',
  textSecondary: '--colors-foreground-fg-gray-600',
  backgroundDefault: '--colors-background-bg-white-base',
  backgroundPaper: '--colors-background-bg-white-alt',
  divider: '--colors-border-border-gray-200',
  // Light-mode default (banner backgrounds are dark/saturated in light mode,
  // so banner text needs to be light) — overridden for dark mode below.
  bannerText: '--colors-base-white',
};

// Light-mode-only overrides: BEE's default -600 warning/success shades fail
// WCAG AA contrast for normal text against a white background (measured
// 3.96:1 and 3.91:1; AA requires 4.5:1 for normal-size text). The darker
// -700 warning shade and the -700 success *foreground* alias both pass
// (5.77:1 / 5.69:1). Dark mode's -600 shades already pass AA against the
// dark background (6.37:1 / 7.27:1), so no override is needed there.
const LIGHT_MODE_VAR_NAME_OVERRIDES: Partial<Record<keyof EdsColorTokens, string>> = {
  warningMain: '--colors-warning-700',
  successMain: '--colors-foreground-fg-success-700',
};

// Dark-mode-only overrides: dark mode's banner backgrounds (brand/error/
// warning) are lighter, more pastel shades than light mode's, so they need
// dark banner text instead of light — measured 6.21:1/5.34:1/6.37:1 for
// --colors-base-black against dark mode's info/error/warning banner
// backgrounds respectively (all pass AA; white fails all three).
const DARK_MODE_VAR_NAME_OVERRIDES: Partial<Record<keyof EdsColorTokens, string>> = {
  bannerText: '--colors-base-black',
};

const FALLBACKS: Record<EdsMode, EdsColorTokens> = {
  light: {
    brandMain: '#1153d6',
    brandDark: '#003cb2',
    errorMain: '#d92d20',
    errorDark: '#b42318',
    warningMain: '#b93815',
    successMain: '#067647',
    secondaryMain: '#344054',
    textPrimary: '#101828',
    textSecondary: '#475467',
    backgroundDefault: '#ffffff',
    backgroundPaper: '#ffffff',
    divider: '#eaecf0',
    bannerText: '#ffffff',
  },
  dark: {
    brandMain: '#528bff',
    brandDark: '#84adff',
    errorMain: '#f04438',
    errorDark: '#f97066',
    warningMain: '#ef6820',
    successMain: '#17b26a',
    secondaryMain: '#cecfd2',
    textPrimary: '#f0f1f1',
    textSecondary: '#94969c',
    backgroundDefault: '#04080f',
    backgroundPaper: '#161b26',
    divider: '#1f242f',
    bannerText: '#04080f',
  },
};

function isResolvedValue(value: string): boolean {
  // A genuinely resolved computed style value is never empty and never a
  // bare `var()` reference (which is what an unresolved/not-yet-loaded
  // custom property, or JSDOM's limited implementation, returns verbatim).
  return value.length > 0 && !value.includes('var(');
}

/**
 * Resolves the EDS color tokens for the given mode. Requires
 * `html.theme-{mode}` to already be set on `document.documentElement` (see
 * EdsThemeProvider) so the browser cascades the correct semantic mapping.
 */
export function resolveEdsColorTokens(mode: EdsMode): EdsColorTokens {
  const fallback = FALLBACKS[mode];

  if (typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') {
    return fallback;
  }

  const computed = window.getComputedStyle(document.documentElement);
  const resolved = {} as EdsColorTokens;

  (Object.keys(fallback) as Array<keyof EdsColorTokens>).forEach(key => {
    const modeOverrides =
      mode === 'light' ? LIGHT_MODE_VAR_NAME_OVERRIDES : DARK_MODE_VAR_NAME_OVERRIDES;
    const varName = modeOverrides[key] ?? DEFAULT_VAR_NAMES[key];
    const value = computed.getPropertyValue(varName).trim();
    resolved[key] = isResolvedValue(value) ? value : fallback[key];
  });

  return resolved;
}
